import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { firebaseService } from '../services/firebaseService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [notice, setNotice] = useState('');
  const handledMagicLinkRef = useRef(false);
  const sendingMagicLinkRef = useRef(false);
  
  const { loginWithFirebaseEmail, loginWithMagicLink, sendMagicLink, loginWithGoogle, sendPasswordReset, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';
  
  // Use local error or context error
  const displayError = localError || error;

  useEffect(() => {
    // Only clear error on component mount
    clearError();
    setLocalError(null);
    setNotice('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const completeMagicLinkSignIn = async () => {
      const currentUrl = window.location.href;
      if (!firebaseService.isMagicLinkSignIn(currentUrl) || handledMagicLinkRef.current) {
        return;
      }

      const currentParams = new URL(currentUrl).searchParams;
      const mode = (currentParams.get('mode') || '').toLowerCase();
      if (mode === 'invite') {
        navigate(`/account-setup${window.location.search}`, { replace: true });
        return;
      }

      handledMagicLinkRef.current = true;

      setIsLoading(true);
      clearError();
      setLocalError(null);
      setNotice('');

      const url = new URL(window.location.href);
      const emailFromQuery = url.searchParams.get('email');
      const emailFromStorage = localStorage.getItem('emailForSignIn');
      const email = (emailFromQuery || emailFromStorage || '').trim().toLowerCase();

      if (!email) {
        setLocalError('Magic link is missing the target email. Ask admin to send a new invitation.');
        setIsLoading(false);
        return;
      }

      const result = await loginWithMagicLink(email, currentUrl);
      if (result.success) {
        localStorage.removeItem('emailForSignIn');
        navigate(from, { replace: true });
      } else {
        setLocalError(result.error || 'Magic link sign-in failed.');
        // Prevent re-processing a spent/invalid action code on subsequent renders.
        window.history.replaceState({}, document.title, '/login');
      }

      setIsLoading(false);
    };

    completeMagicLinkSignIn();
  }, [clearError, from, loginWithMagicLink, navigate]);
  
  // Sync context error to local error for display
  useEffect(() => {
    if (error) {
      setLocalError(error);
    } else {
      setLocalError(null);
    }
  }, [error]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (displayError) {
      clearError();
      setLocalError(null);
    }
    if (notice) {
      setNotice('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    clearError(); // Clear any previous errors
    setLocalError(null);

    try {
      localStorage.setItem('emailForSignIn', formData.email.trim().toLowerCase());
      const result = await loginWithFirebaseEmail(formData.email, formData.password);
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        // Error is already set in AuthContext, but also set locally to ensure display
        const errorMessage = result.error || 'Login failed. Please check your credentials.';
        console.error('Login failed:', errorMessage);
        setLocalError(errorMessage);
      }
    } catch (error) {
      console.error('Login error:', error);
      // Error should already be set in AuthContext via loginWithFirebaseEmail
      // But also set locally as fallback
      const errorMessage = error?.message || error?.error || 'Login failed. Please check your credentials.';
      setLocalError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const result = await loginWithGoogle();
      if (result.success) {
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const email = formData.email.trim().toLowerCase();
    if (!email) {
      setLocalError('Enter your email first, then click Forgot password.');
      return;
    }

    setIsLoading(true);
    clearError();
    setLocalError(null);
    setNotice('');

    const result = await sendPasswordReset(email);
    if (result.success) {
      setNotice('Password reset email sent. Check your inbox.');
    } else {
      setLocalError(result.error || 'Failed to send password reset email.');
    }

    setIsLoading(false);
  };

  const handleSendMagicLink = async () => {
    if (sendingMagicLinkRef.current) {
      return;
    }

    const email = formData.email.trim().toLowerCase();
    if (!email) {
      setLocalError('Enter your email first, then click Send magic link.');
      return;
    }

    sendingMagicLinkRef.current = true;
    setIsLoading(true);
    clearError();
    setLocalError(null);
    setNotice('Sending magic link...');

    try {
      const result = await sendMagicLink(email);
      if (result.success) {
        setNotice('Magic link request accepted. Check inbox/spam for the email, then open the link to sign in. If it does not arrive, contact admin to verify your account and domain settings.');
      } else {
        setNotice('');
        setLocalError(result.error || 'Failed to send magic link email.');
      }
    } finally {
      sendingMagicLinkRef.current = false;
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-600">
            <span className="text-2xl font-bold text-white">B</span>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Bridge Maintenance Budgeting
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to your account
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email" className="sr-only">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div className="relative">
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm pr-10"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <FontAwesomeIcon icon={faEyeSlash} className="h-5 w-5 text-gray-400" />
                  ) : (
                    <FontAwesomeIcon icon={faEye} className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                'Sign in'
              )}
            </button>

            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={isLoading}
              className="w-full text-sm font-medium text-primary-700 hover:text-primary-800 disabled:opacity-50"
            >
              Forgot password?
            </button>
          </div>

          {displayError && (
            <div className="rounded-md bg-red-50 p-4 border border-red-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    {displayError}
                  </h3>
                </div>
              </div>
            </div>
          )}

          {notice && (
            <div className="rounded-md bg-green-50 p-4 border border-green-200">
              <p className="text-sm font-medium text-green-800">{notice}</p>
            </div>
          )}

          <div className="space-y-3">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 text-gray-500 font-medium">Alternative sign-in options:</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleSendMagicLink}
                disabled={isLoading}
                className="w-full min-h-[72px] flex items-center justify-center text-center px-4 border border-primary-300 text-sm font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send me a magic link
              </button>

              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className="w-full min-h-[72px] flex items-center justify-center gap-2 px-4 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span>Google</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login; 